import { useState } from "react";

import { Dropdown } from "../../../common/Atoms/Dropdown";
import { useInvoiceTags } from "../../hooks/useInvoiceTags";
import { Modal } from "../../../common/Atoms/Modal";
import { InputTextAddon } from "../../../common/Atoms/InputTextAddon";
import { Button } from "../../../common/Atoms/Button";
import {
  useApplyInvoiceTagMutation,
  useClearInvoiceTagsMutation,
  useCreateInvoiceTagMutation,
} from "../../services/api/invoiceTagsApi/invoiceTags";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { TagColorPicker, randomTagColor } from "../Molecules/TagColorPicker";

interface IApplyInvoiceTagsProps {
  selectedInvoices: string[];
}

const NEW_TAG = `CREATE_NEW_TAG_OPTION_VALUE`;
const CLEAR_TAGS = `CLEAR_TAGS_OPTION_VALUE`;

export function ApplyInvoiceTags({ selectedInvoices }: IApplyInvoiceTagsProps) {
  const { tags, isLoading } = useInvoiceTags();
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [applyTag, { isLoading: applyLoading }] = useApplyInvoiceTagMutation();
  const [clearTags, { isLoading: clearLoading }] = useClearInvoiceTagsMutation();
  const currentOrg = useGetSelectedOrganisation();

  const tagOptions = tags
    .map(tag => ({
      value: tag,
      label: tag,
    }));

  function handleSelect(e: { value: string; label: string }) {
    if (e.value === NEW_TAG) {
      setShowNewTagInput(true);

      return;
    }

    if (e.value === CLEAR_TAGS) {
      clearTags({
        organisationId: currentOrg.id,
        invoiceIds: selectedInvoices,
      });

      return;
    }

    applyTag({
      organisationId: currentOrg.id,
      invoiceIds: selectedInvoices,
      tag: e.value,
    });
  }

  return (
    <>
      <CreateInvoiceTagModal
        open={ showNewTagInput }
        onClose={ () => setShowNewTagInput(false) }
        selectedInvoices={ selectedInvoices }
      />
      <div>
        <Dropdown
          buttonColor={ `white` }
          options={ [
            [
              { value: NEW_TAG, label: `Create new tag` },
              { value: CLEAR_TAGS, label: `Clear current tags` },
            ],
            tagOptions,
          ] }
          onSelect={ handleSelect }
          label={ `Apply tags` }
          size={ `md` }
          disabled={ !selectedInvoices.length }
          loading={ isLoading || applyLoading || clearLoading }
        />
      </div>
    </>
  );
}

function CreateInvoiceTagModal({ open, onClose, selectedInvoices }: { open: boolean; onClose: () => void; selectedInvoices: string[] }) {
  const [newTag, setNewTag] = useState(``);
  const [newColor, setNewColor] = useState(randomTagColor);
  const [createTag, { isLoading: isCreating }] = useCreateInvoiceTagMutation();
  const currentOrg = useGetSelectedOrganisation();
  const { refetch } = useInvoiceTags();

  async function save() {
    const res = await createTag({
      organisationId: currentOrg.id,
      tagName: newTag,
      selectedInvoices,
      color: newColor,
    });

    if (!Object.keys(res).includes(`error`)) {
      refetch();
      setNewTag(``);
      setNewColor(randomTagColor());
      onClose();

      return;
    }
  }

  return (
    <Modal
      open={ open }
      setOpen={ onClose }
      title={ `Create A New Invoice Tag` }
    >
      <div className={ `space-y-4 px-4` }>
        <div className={ `flex justify-between items-end` }>
          <InputTextAddon
            value={ newTag }
            onChange={ e => setNewTag(e.value.toString()) }
            placeholder={ `Enter a new tag name` }
            label={ `Tag Name` }
            className={ `w-full mr-6` }
          />

          <Button
            onClick={ save }
            className={ `h-full mt-0` }
            loading={ isCreating }
            disabled={ !newTag || isCreating }
          >
            { `Save` }
          </Button>
        </div>

        <TagColorPicker
          value={ newColor }
          onChange={ setNewColor }
        />
      </div>
    </Modal>
  );
}
